import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cotizacionesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime } from '../utils/dateHelpers';

const CotizacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef();

  const [cotizacion, setCotizacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [nuevoSeguimiento, setNuevoSeguimiento] = useState({ tipo: 'llamada', descripcion: '' });

  useEffect(() => {
    cargarCotizacion();
  }, [id]);

  const cargarCotizacion = async () => {
    setLoading(true);
    try {
      const data = await cotizacionesApi.getById(id);
      setCotizacion(data);
    } catch (error) {
      console.error('Error al cargar cotización:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatearFecha = formatDate;

  const formatearMoneda = (valor) => {
    return `Q.${parseFloat(valor || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const windowPrint = window.open('about:blank', '_blank', 'width=800,height=600');

    if (!windowPrint) {
      alert('Por favor habilite las ventanas emergentes para imprimir');
      return;
    }

    windowPrint.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Cotización ${cotizacion?.numero_cotizacion || ''}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #333; }
            ${getEstilosImpresion()}
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.addEventListener('load', function() {
              setTimeout(function() {
                window.print();
              }, 500);
            });
          </script>
        </body>
      </html>
    `);
    windowPrint.document.close();
  };

  const getEstilosImpresion = () => `
    .cotizacion-pdf {
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      background: white;
    }
    .pdf-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 30px;
      padding-bottom: 20px;
      border-bottom: 3px solid #e65725;
    }
    .pdf-logo img {
      max-width: 180px;
      height: auto;
    }
    .pdf-info-table {
      border-collapse: collapse;
      font-size: 12px;
    }
    .pdf-info-table th,
    .pdf-info-table td {
      border: 1px solid #ddd;
      padding: 8px 12px;
    }
    .pdf-info-table th {
      background: #f8f9fa;
      text-align: left;
      font-weight: 600;
    }
    .pdf-cliente {
      margin-bottom: 30px;
    }
    .pdf-cliente-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 13px;
    }
    .pdf-cliente-table th,
    .pdf-cliente-table td {
      border: 1px solid #ddd;
      padding: 10px 12px;
    }
    .pdf-cliente-table th {
      background: #e65725;
      color: white;
      font-weight: 600;
      width: 120px;
    }
    .pdf-items {
      margin-bottom: 30px;
    }
    .pdf-items-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    .pdf-items-table th {
      background: #e65725;
      color: white;
      padding: 12px;
      font-weight: 600;
      text-align: left;
    }
    .pdf-items-table td {
      border: 1px solid #ddd;
      padding: 12px;
      vertical-align: top;
    }
    .pdf-items-table .item-image {
      width: 80px;
    }
    .pdf-items-table .item-image img {
      max-width: 70px;
      max-height: 70px;
      object-fit: contain;
    }
    .pdf-items-table .item-desc {
      font-size: 11px;
      color: #666;
      margin-top: 4px;
    }
    .pdf-items-table .text-right {
      text-align: right;
    }
    .pdf-items-table .text-center {
      text-align: center;
    }
    .pdf-totales {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 30px;
    }
    .pdf-totales-box {
      width: 300px;
    }
    .pdf-totales-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid #ddd;
    }
    .pdf-totales-row.total {
      background: #e65725;
      color: white;
      font-weight: bold;
      font-size: 16px;
    }
    .pdf-mensaje {
      text-align: center;
      font-style: italic;
      margin-bottom: 40px;
      padding: 20px;
      background: #f8f9fa;
      border-radius: 8px;
    }
    .pdf-firma {
      text-align: center;
      margin-bottom: 40px;
    }
    .pdf-firma-linea {
      width: 200px;
      border-bottom: 1px solid #333;
      margin: 0 auto 10px;
    }
    .pdf-firma-nombre {
      font-weight: bold;
    }
    .pdf-firma-telefono {
      font-size: 12px;
      color: #666;
    }
    .pdf-footer {
      display: flex;
      justify-content: center;
      gap: 30px;
      padding-top: 20px;
      border-top: 2px solid #e65725;
      font-size: 12px;
    }
    .pdf-footer-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .wave-decoration {
      height: 20px;
      background: linear-gradient(90deg, #e65725, #e74c3c, #e65725);
      border-radius: 0 0 50% 50% / 0 0 100% 100%;
      margin-bottom: 20px;
    }
  `;

  const handleEnviarWhatsapp = async () => {
    if (!cotizacion?.cliente_telefono) {
      alert('El cliente no tiene número de teléfono registrado');
      return;
    }

    // Registrar el envío
    try {
      await cotizacionesApi.registrarEnvioWhatsapp({
        id: cotizacion.id,
        usuario_id: user?.id
      });
      cargarCotizacion();
    } catch (error) {
      console.error('Error al registrar envío:', error);
    }

    // Generar mensaje
    const mensaje = `Hola ${cotizacion.cliente_nombre},

Le enviamos su cotización *${cotizacion.numero_cotizacion}*

*Detalle:*
${cotizacion.items?.map(item =>
  `- ${item.nombre} (${item.cantidad}) - ${formatearMoneda(item.precio_total)}`
).join('\n')}

*Total: ${formatearMoneda(cotizacion.total)}*

Vigencia: ${cotizacion.validez_dias} días

${cotizacion.mensaje_cotizacion || ''}

_${cotizacion.nombre_tienda}_`;

    const telefono = cotizacion.cliente_telefono.replace(/\D/g, '');
    window.open(`https://wa.me/502${telefono}?text=${encodeURIComponent(mensaje)}`, '_blank');
  };

  const handleCambiarEstado = async () => {
    try {
      await cotizacionesApi.cambiarEstado({
        id: cotizacion.id,
        estado: nuevoEstado,
        usuario_id: user?.id
      });
      setShowEstadoModal(false);
      cargarCotizacion();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  const handleAgregarSeguimiento = async () => {
    try {
      await cotizacionesApi.agregarSeguimiento({
        cotizacion_id: cotizacion.id,
        ...nuevoSeguimiento,
        usuario_id: user?.id
      });
      setShowSeguimientoModal(false);
      setNuevoSeguimiento({ tipo: 'llamada', descripcion: '' });
      cargarCotizacion();
    } catch (error) {
      console.error('Error al agregar seguimiento:', error);
    }
  };

  const handleDuplicar = async () => {
    try {
      const response = await cotizacionesApi.duplicar({
        id: cotizacion.id,
        creado_por: user?.id
      });
      navigate(`/cotizaciones/${response.id}`);
    } catch (error) {
      console.error('Error al duplicar:', error);
    }
  };

  const getEstadoClass = (estado) => {
    const clases = {
      pendiente: 'status-pendiente',
      enviada: 'status-proceso',
      aceptada: 'status-terminada',
      rechazada: 'status-entregada',
      vencida: 'status-badge'
    };
    return clases[estado] || 'status-badge';
  };

  const getTipoSeguimientoIcon = (tipo) => {
    const icons = {
      creacion: '📝',
      envio_whatsapp: '📱',
      llamada: '📞',
      email: '📧',
      visita: '🏠',
      nota: '📋',
      estado_cambio: '🔄'
    };
    return icons[tipo] || '📋';
  };

  const getDisponibilidadInfo = (disponibilidad) => {
    const info = {
      disponible: { label: 'Disponible', color: '#28a745', bg: '#d4edda' },
      agotado: { label: 'Agotado', color: '#dc3545', bg: '#f8d7da' },
      bajo_pedido: { label: 'Bajo Pedido', color: '#fd7e14', bg: '#fff3cd' }
    };
    return info[disponibilidad] || info.disponible;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!cotizacion) {
    return (
      <div className="empty-state">
        <h3>Cotización no encontrada</h3>
        <button className="btn btn-primary" onClick={() => navigate('/cotizaciones')}>
          Volver al listado
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cotización {cotizacion.numero_cotizacion}</h1>
          <p className="page-subtitle">
            <span className={`status-badge ${getEstadoClass(cotizacion.estado)}`}>
              {cotizacion.estado?.toUpperCase()}
            </span>
            {' '}Creada el {formatearFecha(cotizacion.fecha_emision)}
          </p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/cotizaciones')}>
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="btn-text">Volver</span>
          </button>
          <button className="btn btn-secondary" onClick={handleDuplicar}>
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
            </svg>
            <span className="btn-text">Duplicar</span>
          </button>
          {cotizacion.estado === 'pendiente' && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/cotizaciones/editar/${cotizacion.id}`)}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span className="btn-text">Editar</span>
            </button>
          )}
        </div>
      </div>

      <div className="cotizacion-detalle-grid">
        {/* Cotización PDF */}
        <div className="card cotizacion-preview-card">
          <div className="cotizacion-preview-header">
            <button className="btn btn-primary" onClick={handlePrint}>
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimir
            </button>
            <button className="btn btn-success" onClick={handleEnviarWhatsapp}>
              <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Enviar WhatsApp
            </button>
          </div>

          <div ref={printRef} className="cotizacion-pdf-wrapper">
            <div className="cotizacion-pdf">
              {/* Encabezado */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '30px',
                paddingBottom: '20px',
                borderBottom: '3px solid #e65725'
              }}>
                <div>
                  <img
                    src="/logo-intersecom.png"
                    alt="Intersecom"
                    style={{ maxWidth: '180px' }}
                  />
                </div>
                <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                  <tbody>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px 12px', background: '#f8f9fa', textAlign: 'left' }}>Cliente</th>
                      <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{cotizacion.cliente_nombre}</td>
                    </tr>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px 12px', background: '#f8f9fa', textAlign: 'left' }}>Dirección</th>
                      <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{cotizacion.cliente_direccion || '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px 12px', background: '#f8f9fa', textAlign: 'left' }}>Teléfono</th>
                      <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{cotizacion.cliente_telefono || '-'}</td>
                    </tr>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px 12px', background: '#f8f9fa', textAlign: 'left' }}>Fecha</th>
                      <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{formatearFecha(cotizacion.fecha_emision)}</td>
                    </tr>
                    <tr>
                      <th style={{ border: '1px solid #ddd', padding: '8px 12px', background: '#f8f9fa', textAlign: 'left' }}>Validez</th>
                      <td style={{ border: '1px solid #ddd', padding: '8px 12px' }}>{cotizacion.validez_dias} días</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Tabla de items */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '20px' }}>
                <thead>
                  <tr>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'left' }}>Imagen</th>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'left' }}>Descripción</th>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'center' }}>Cantidad</th>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'right' }}>Precio Único</th>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'right' }}>Total</th>
                    <th style={{ background: '#e65725', color: 'white', padding: '12px', fontWeight: '600', textAlign: 'center' }}>Disponibilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizacion.items?.map((item, index) => {
                    const dispInfo = getDisponibilidadInfo(item.disponibilidad);
                    return (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '12px', width: '80px' }}>
                        {item.imagen_url && (
                          <img
                            src={item.imagen_url}
                            alt={item.nombre}
                            style={{ maxWidth: '70px', maxHeight: '70px', objectFit: 'contain' }}
                          />
                        )}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '12px', verticalAlign: 'top' }}>
                        <strong>{item.nombre}</strong>
                        {item.descripcion && (
                          <div style={{ fontSize: '11px', color: '#666', marginTop: '4px', whiteSpace: 'pre-line' }}>
                            {item.descripcion}
                          </div>
                        )}
                      </td>
                      <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center' }}>{item.cantidad}</td>
                      <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'right' }}>{formatearMoneda(item.precio_unitario)}</td>
                      <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'right' }}>{formatearMoneda(item.precio_total)}</td>
                      <td style={{ border: '1px solid #ddd', padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          background: dispInfo.bg,
                          color: dispInfo.color
                        }}>
                          {dispInfo.label}
                        </span>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>

              {/* Totales */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
                <div style={{ width: '250px' }}>
                  {parseFloat(cotizacion.descuento_porcentaje) > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                        <span>Subtotal:</span>
                        <span>{formatearMoneda(cotizacion.subtotal)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #ddd' }}>
                        <span>Descuento ({cotizacion.descuento_porcentaje}%):</span>
                        <span>-{formatearMoneda(cotizacion.descuento_monto)}</span>
                      </div>
                    </>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: '#e65725', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                    <span>TOTAL:</span>
                    <span>{formatearMoneda(cotizacion.total)}</span>
                  </div>
                </div>
              </div>

              {/* Mensaje */}
              {cotizacion.mensaje_cotizacion && (
                <div style={{
                  textAlign: 'center',
                  fontStyle: 'italic',
                  marginBottom: '40px',
                  padding: '20px',
                  background: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  {cotizacion.mensaje_cotizacion}
                </div>
              )}

              {/* Firma */}
              <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <div style={{ width: '200px', borderBottom: '1px solid #333', margin: '0 auto 10px' }}></div>
                <div style={{ fontWeight: 'bold' }}>{cotizacion.nombre_creador || 'Asesor de Ventas'}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>{cotizacion.tienda_telefono}</div>
              </div>

              {/* Términos y Condiciones */}
              <div style={{
                marginBottom: '30px',
                padding: '20px',
                background: '#fff5f0',
                border: '1px solid #e65725',
                borderRadius: '8px',
                fontSize: '11px',
                lineHeight: '1.6'
              }}>
                <h3 style={{
                  fontSize: '13px',
                  fontWeight: 'bold',
                  color: '#e65725',
                  marginBottom: '12px',
                  textAlign: 'center'
                }}>TÉRMINOS Y CONDICIONES DE COTIZACIÓN</h3>

                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: '#e65725' }}>1. Validez:</strong><br />
                  La cotización es válida únicamente en la fecha indicada. Precios y descuentos sujetos a forma de pago y promociones vigentes.
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: '#e65725' }}>2. Formas de pago:</strong><br />
                  Efectivo, Transferencia, Anticipado, y tarjetas de crédito.<br />
                  Pagos por cheque o transferencia pueden requerir verificación previa.
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: '#e65725' }}>3. Entrega:</strong><br />
                  De inmediata según forma de pago.<br />
                  Si no hay inventario, el plazo puede extenderse hasta 3 días.
                </div>

                <div>
                  <strong style={{ color: '#e65725' }}>4. Garantía:</strong><br />
                  Cubre únicamente defectos de fábrica.<br />
                  No aplica por daños físicos, mal uso o software/consumibles.<br />
                  Reparación o reemplazo según criterio del servicio técnico.
                </div>
              </div>

              {/* Footer */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '30px',
                paddingTop: '20px',
                borderTop: '2px solid #e65725',
                fontSize: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📍</span>
                  <span>{cotizacion.tienda_direccion}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📞</span>
                  <span>{cotizacion.tienda_telefono}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Panel lateral */}
        <div className="cotizacion-sidebar">
          {/* Acciones */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Acciones</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onClick={() => setShowEstadoModal(true)}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Cambiar Estado
              </button>
              <button
                className="btn btn-secondary"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onClick={() => setShowSeguimientoModal(true)}
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Agregar Seguimiento
              </button>
            </div>
          </div>

          {/* Historial de seguimientos */}
          <div className="card" style={{ marginTop: '16px' }}>
            <div className="card-header">
              <h3 className="card-title">Historial</h3>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {cotizacion.seguimientos?.length === 0 ? (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>Sin seguimientos</p>
              ) : (
                cotizacion.seguimientos?.map((seg, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      borderBottom: index < cotizacion.seguimientos.length - 1 ? '1px solid #eee' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span>{getTipoSeguimientoIcon(seg.tipo)}</span>
                      <strong style={{ fontSize: '13px' }}>{seg.tipo?.replace('_', ' ')}</strong>
                    </div>
                    {seg.descripcion && (
                      <p style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>{seg.descripcion}</p>
                    )}
                    <div style={{ fontSize: '11px', color: '#999' }}>
                      {seg.nombre_usuario || 'Sistema'} - {formatDateTime(seg.created_at)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal cambiar estado */}
      {showEstadoModal && (
        <div className="modal-overlay" onClick={() => setShowEstadoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Cambiar Estado</h3>
              <button className="modal-close" onClick={() => setShowEstadoModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Nuevo Estado</label>
                <select
                  className="form-control"
                  value={nuevoEstado}
                  onChange={(e) => setNuevoEstado(e.target.value)}
                >
                  <option value="">Seleccionar...</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="enviada">Enviada</option>
                  <option value="aceptada">Aceptada</option>
                  <option value="rechazada">Rechazada</option>
                  <option value="vencida">Vencida</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEstadoModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleCambiarEstado} disabled={!nuevoEstado}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal agregar seguimiento */}
      {showSeguimientoModal && (
        <div className="modal-overlay" onClick={() => setShowSeguimientoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Agregar Seguimiento</h3>
              <button className="modal-close" onClick={() => setShowSeguimientoModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Tipo de Seguimiento</label>
                <select
                  className="form-control"
                  value={nuevoSeguimiento.tipo}
                  onChange={(e) => setNuevoSeguimiento(prev => ({ ...prev, tipo: e.target.value }))}
                >
                  <option value="llamada">Llamada telefónica</option>
                  <option value="email">Correo electrónico</option>
                  <option value="visita">Visita presencial</option>
                  <option value="nota">Nota general</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea
                  className="form-control"
                  value={nuevoSeguimiento.descripcion}
                  onChange={(e) => setNuevoSeguimiento(prev => ({ ...prev, descripcion: e.target.value }))}
                  rows="3"
                  placeholder="Detalles del seguimiento..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowSeguimientoModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleAgregarSeguimiento}>
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CotizacionDetalle;
